<template>
	<div class="poll-options-container">
		<div class="title">
			{{ t('polls_options_section_title') }}
		</div>
		<ul ref="parent" class="option-list">
			<li
				v-for="(option, index) in options"
				:key="option.id"
				class="option-item"
			>
				<Input
					v-model="option.text"
					:placeholder="t('polls_option_placeholder', { index: index + 1 })"
					class="option-input"
					@keydown.enter.prevent="handleKey(index)"
					@focus="handleFocus(index)"
					@blur="handleBlur(index)"
					ref="inputs"
					:key="option.id + 'input'"
				/>
				<BarsIcon type="regular" class="drag-handle" />
			</li>
		</ul>
		<div v-if="filledCount === 0" class="option-limit">
			{{ t('polls_options_footer_max', { max_options: maxOptions }) }}
		</div>
		<div v-else-if="filledCount !== maxOptions" class="option-limit">
			{{
				t(
					'polls_options_footer_remaining.variable',
					{ remaining_options: remaining },
					remaining
				)
			}}
		</div>
	</div>
</template>

<script setup>
import { watch, computed, nextTick, ref } from 'vue'
import { useDragAndDrop } from '@formkit/drag-and-drop/vue'
import { Input } from '@/components/common'
import { BarsIcon } from '@/icons'
import { useI18n } from 'vue-i18n'
import { useTemplateRef } from 'vue'

const { t } = useI18n()

const minOptions = 2
const maxOptions = 10
let optionId = 0
const createOption = (text = '') => ({ id: optionId++, text, focused: false })

const [parent, options] = useDragAndDrop([createOption(), createOption()], {
	handle: '.drag-handle',
})
const inputs = useTemplateRef('inputs')
const currentlyFocusedIndex = ref(-1)

// Computed properties
const filledCount = computed(
	() => options.value.filter((opt) => opt.text.trim() !== '').length
)

const remaining = computed(() => Math.max(0, maxOptions - filledCount.value))

const getCurrentOptions = () => {
	return options.value
		.filter((opt) => opt.text.trim() !== '')
		.map((opt) => ({ text: opt.text }))
}

// Expose methods to parent component
defineExpose({
	getCurrentOptions,
})

// Auto-add logic: Watch for text changes specifically, not array structure changes
watch(
	() => options.value.map((opt) => opt.text),
	async (newTexts, oldTexts) => {
		// Only proceed if this is actually a text change, not a drag reorder
		if (!oldTexts || newTexts.length !== oldTexts.length) {
			return
		}

		const lastOption = options.value[options.value.length - 1]
		const isLastOptionFilled = lastOption && lastOption.text.trim() !== ''
		const canAddMore = options.value.length < maxOptions

		// Auto-add: If last option becomes non-empty and we can add more
		if (isLastOptionFilled && canAddMore) {
			options.value.push(createOption())
			await nextTick()
		}
	}
)

// Methods
function handleFocus(index) {
	options.value[index].focused = true
	currentlyFocusedIndex.value = index
}

function handleBlur(index) {
	options.value[index].focused = false

	setTimeout(async () => {
		const focusedIndex = currentlyFocusedIndex.value

		// Don't clean up the currently focused option
		await cleanupEmptyOptions(focusedIndex)
	}, 10)
}

async function cleanupEmptyOptions(protectedIndex = -1) {
	if (options.value.length <= minOptions) return

	const indicesToRemove = []

	for (let i = options.value.length - 1; i >= 0; i--) {
		const option = options.value[i]
		const isProtected = i === protectedIndex

		if (
			!isProtected &&
			option.text.trim() === '' &&
			!option.focused &&
			options.value.length - indicesToRemove.length > minOptions
		) {
			indicesToRemove.push(i)
		}
	}

	for (const index of indicesToRemove) {
		options.value.splice(index, 1)
	}

	const allFilled = options.value.every((opt) => opt.text.trim() !== '')
	const needsEmptyOption = allFilled && options.value.length < maxOptions

	if (needsEmptyOption) {
		options.value.push(createOption())
	}

	await nextTick()
}

async function handleKey(index) {
	await nextTick()

	if (inputs.value?.[index + 1]) {
		inputs.value[index + 1].focus()
	}
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';
.poll-options-container {
	.title {
		font-family: 'Avenir-Heavy';
		font-size: 22px;
		color: var(--neutral-800);
	}
	.option-list {
		list-style: none;
		padding: 0;
	}
	.option-item {
		position: relative;
		display: flex;
		align-items: center;
		:deep(.pronto-input) {
			border-radius: 0;
			background-color: var(--neutral-100);
			width: 100%;
			padding-right: 2rem;
			// border-bottom: none;
		}

		&:first-child {
			:deep(.pronto-input) {
				border-top-left-radius: 6px;
				border-top-right-radius: 6px;
			}
		}

		&:last-child {
			:deep(.pronto-input) {
				border-bottom-left-radius: 6px;
				border-bottom-right-radius: 6px;
				// border-bottom: 1px solid var(--neutral-200);
				// &.focused {
				//     border-bottom: 1px solid var(--blue-300);
				// }
			}
		}
	}

	.option-input {
		width: 100%;
		font-size: 1rem;
		flex: 1;
	}

	.drag-handle {
		position: absolute;
		right: 0.5rem;
		top: 50%;
		transform: translateY(-50%);
		cursor: grab;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 18px;
		color: var(--neutral-400);
	}
	.option-limit {
		font-size: 14px;
		color: var(--neutral-600);
		font-family: 'Avenir-Roman';
	}
}
</style>
